import React, { useEffect, useState } from "react";
import { Button, Divider, Flex, Form, Select, Typography } from "antd";

import classes from "./Finance.module.css";
import { useForm } from "antd/es/form/Form";
import dayjs from "dayjs";

import { useParams } from "react-router-dom";
import {
  useLazyGetAccountingBalanceQuery,
  useUpdateAccountingBalanceMutation,
} from "../../../../store/api/applicationBankApi/finance.api";
import { useGetApplicationDataToBankQuery } from "../../../../store/api/applicationBankApi/applicationBank.api";
import { FinanceTable } from "./ui/FinanceTable/FinanceTable";
import { Guard } from "../../../../utils/guards/Guard";
import { USER } from "../../../../types";
import { calculateSumRows } from "./utils/calculateSumRows";
import {
  BalanceRows,
  getFormattedBalanceData,
} from "./utils/getFormattedBalanceData";
import { YearSelectOptions } from "./ui/YearSelectOptions/YearSelectOptions";
import { formatRowKey } from "./utils/formatRowKey";
import { APPLICATION_BANK_STATUS } from "../../../../utils/guards/ApplicationBankStatusGuard/types";
import { useAppSelector } from "../../../../store/reduxHooks";
import { SELECT_PROFILE_ROLE } from "../../../../store/selectors/profileSelectors";
import { FieldData } from "../../../../store/types/appTypes";
import { NamePath } from "antd/es/form/interface";

const { Text } = Typography;

export const Finance = () => {
  const userRole = useAppSelector(SELECT_PROFILE_ROLE);
  const [form] = useForm();
  const [isYearSelected, setIsYearSelected] = useState(false);
  const { applicationBankId } = useParams();
  const { data: applicationBankData } = useGetApplicationDataToBankQuery(
    Number(applicationBankId)
  );

  const [
    updateAccountingBalance,
    { isLoading: isLoadingUpdateAccountingBalance },
  ] = useUpdateAccountingBalanceMutation();
  const financeData =
    applicationBankData?.balance_application.accountingBalance;

  const [trigger, loadedData] = useLazyGetAccountingBalanceQuery({});

  const formattedBalanceData = getFormattedBalanceData(financeData);

  const initialValues = {
    accountingBalance: {
      year: dayjs(financeData?.balance_date_lc).get("year"),
      data: formattedBalanceData,
    },
  };

  // обновить данные в таблице если с бэка пришли обновленные данные
  useEffect(() => {
    form.resetFields();
    if (form.getFieldValue(["accountingBalance", "year"])) {
      setIsYearSelected(true);
    }
  }, [financeData, form]);

  // обновить данные в таблице при загрузке из внешних источников
  useEffect(() => {
    if (loadedData.currentData) {
      const newValues = { accountingBalance: { data: {} as BalanceRows } };

      for (const key in loadedData.currentData) {
        newValues.accountingBalance.data[formatRowKey(key)] = {
          lc: loadedData.currentData[key] / 1000, // делим загруженные значения на 1000, потому что расчеты в тысячах
        };
      }

      form.setFieldsValue(newValues);
    }
  }, [loadedData, form]);

  // Загрузить данные из внешних источников
  const handleLoadAccountingBalanceData = () => {
    const year = form.getFieldValue(["accountingBalance", "year"]);
    year &&
      trigger({
        year: year,
        applicationBankId: Number(applicationBankId),
      });
  };

  // Сохранить данные
  const handleUpdateAccountingBalance = () => {
    const values = form.getFieldsValue();
    const body = {
      accountingBalance: { data: {} as BalanceRows, balance_date: "" },
    };

    for (const key in values.accountingBalance.data) {
      body.accountingBalance.data[formatRowKey(key)] = {
        lc: values.accountingBalance.data[key].lc || 0,
      };

      body.accountingBalance.balance_date = form.getFieldValue([
        "accountingBalance",
        "year",
      ]);
    }

    updateAccountingBalance({
      applicationBankId: Number(applicationBankId),
      data: body,
    });
  };

  // Обработчик изменений полей в таблице
  const handleFieldsChange = (changedFields: FieldData[]) => {
    if (form.getFieldValue(["accountingBalance", "year"])) {
      setIsYearSelected(true);
    }
    const fieldName = changedFields[0].name as NamePath;
    calculateSumRows(form, Number(fieldName[2]));
  };

  // Деактивируем форму на всех статусах кроме В работе и Формирование предложение, и на всчех ролях кроме Андеррайтера
  const isFormDisabled =
    (applicationBankData?.modified_bank_status.type !==
      APPLICATION_BANK_STATUS.DIRECTED &&
      applicationBankData?.modified_bank_status.type !==
        APPLICATION_BANK_STATUS.OFFER) ||
    userRole !== USER.UNDERWRITER;

  return (
    <Form
      form={form}
      autoComplete="off"
      layout="vertical"
      initialValues={initialValues}
      onFinish={handleUpdateAccountingBalance}
      onFieldsChange={(changedFields) => handleFieldsChange(changedFields)}
    >
      <Divider orientation="left" orientationMargin="0">
        Бухгалтерский баланс
      </Divider>
      <Guard.Role roles={[USER.UNDERWRITER]}>
        <Guard.ApplicationBankStatus
          status={applicationBankData?.modified_bank_status.type}
          allowedStatuses={[
            APPLICATION_BANK_STATUS.DIRECTED,
            APPLICATION_BANK_STATUS.OFFER,
          ]}
        >
          <Flex justify="flex-end" align="center" gap={8}>
            <Form.Item
              style={{ margin: 0, padding: 0 }}
              name={["accountingBalance", "year"]}
            >
              <Select placeholder="Год">{YearSelectOptions()}</Select>
            </Form.Item>
            <Button
              type="default"
              onClick={handleLoadAccountingBalanceData}
              loading={loadedData.isFetching}
              disabled={!isYearSelected}
            >
              Загрузить данные из внешних источников
            </Button>

            <Button
              type="primary"
              onClick={handleUpdateAccountingBalance}
              loading={isLoadingUpdateAccountingBalance}
            >
              Сохранить
            </Button>
          </Flex>
        </Guard.ApplicationBankStatus>
      </Guard.Role>

      <div className={classes.tableContainer}>
        {financeData?.data?.active && (
          <div>
            <Text className={classes.tableName} type="secondary">
              Актив
            </Text>
            <FinanceTable
              dataSource={financeData.data?.active}
              form={form}
              isDisabled={isFormDisabled}
            />
          </div>
        )}

        {financeData?.data?.passive && (
          <div>
            <Text className={classes.tableName} type="secondary">
              Пассив
            </Text>
            <FinanceTable
              dataSource={financeData.data?.passive}
              form={form}
              isDisabled={isFormDisabled}
            />
          </div>
        )}

        {financeData?.data?.report && (
          <div>
            <Text className={classes.tableName} type="secondary">
              Отчет о финансовых результатах
            </Text>
            <FinanceTable
              dataSource={financeData.data?.report}
              form={form}
              isDisabled={isFormDisabled}
            />
          </div>
        )}
      </div>
    </Form>
  );
};
